export type IfasModuleKey =
  | "casePortal"
  | "funeralPlanning"
  | "specialServices"
  | "bodyRemoval"
  | "mortuary"
  | "warehouse"
  | "bodyDispatch"
  | "quotations"
  | "memorials"
  | "tombstone"
  | "catering"
  | "fleet"
  | "drivers"
  | "vehicles"
  | "revenue"
  | "catalogue"
  | "ifasReports";

export type IfasReportKey =
  | "funeral"
  | "planner"
  | "mortuary"
  | "mortuaryTrends"
  | "services"
  | "fleet"
  | "warehouse"
  | "inventoryMovements"
  | "suppliers"
  | "customers"
  | "feedback"
  | "executive"
  ;

export type IfasCapability =
  | "caseInitialCreate"
  | "caseWrite"
  | "caseConvertPolicy"
  | "caseSubmit"
  | "caseAssignSomeone"
  | "plannerBranchManage"
  | "operationalLocationManage"
  | "quotationWrite"
  | "bodyRemovalAssign"
  | "bodyRemovalOperate"
  | "bodyDispatchOperate"
  | "bodyDispatchApprove"
  | "mortuaryAssist"
  | "mortuaryManage"
  | "mortuaryApprove"
  | "warehousePreparationWrite"
  | "warehousePreparationApprove"
  | "warehouseChecklistWrite"
  | "warehouseChecklistApprove"
  | "warehouseReportWrite"
  | "warehouseReportSubmit"
  | "inventoryManage"
  | "fleetManage"
  | "revenueView"
  | "quotationApprove"
  | "invoiceIssue"
  | "paymentConfirm"
  | "catalogueManage";

const normalizeIfasRole = (role: unknown) =>
  String(role || "")
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_");

const ifasRoleConfig: Record<
  string,
  {
    modules: IfasModuleKey[];
    capabilities: IfasCapability[];
  }
> = {
  // The two offices that release a case held under SOP 12.1 (FR-094/095). They
  // approve or reject an Acknowledgement of Debt from the Revenue module, so they
  // need to reach it — but read-only: the office that releases a held case must
  // not also be able to price a catalogue, issue the invoice or confirm the
  // payment behind it. The approve endpoints are enforced server-side by role.
  ifas_gcoo: { modules: ["ifasReports", "revenue"], capabilities: ["revenueView"] },
  ifas_gceo: { modules: ["ifasReports", "revenue"], capabilities: ["revenueView"] },
  executive: { modules: ["ifasReports"], capabilities: [] },
  ifas_administrator: {
    modules: [
      "casePortal",
      "funeralPlanning",
      "specialServices",
      "bodyRemoval",
      "mortuary",
      "warehouse",
      "bodyDispatch",
      "quotations",
      "memorials",
      "tombstone",
      "catering",
      "fleet",
      "drivers",
      "vehicles",
      "revenue",
      "catalogue",
      "ifasReports",
    ],
    capabilities: [
      "caseInitialCreate",
      "caseWrite",
      "caseConvertPolicy",
      "caseSubmit",
      "caseAssignSomeone",
      "plannerBranchManage",
      "operationalLocationManage",
      "quotationWrite",
      "quotationApprove",
      "invoiceIssue",
      "paymentConfirm",
      "revenueView",
      "catalogueManage",
      "bodyRemovalAssign",
      "bodyRemovalOperate",
      "bodyDispatchOperate",
      "bodyDispatchApprove",
      "mortuaryAssist",
      "mortuaryManage",
      "mortuaryApprove",
      "warehousePreparationWrite",
      "warehousePreparationApprove",
      "warehouseChecklistWrite",
      "warehouseChecklistApprove",
      "warehouseReportWrite",
      "warehouseReportSubmit",
      "inventoryManage",
      "fleetManage",
    ], 
  },
  removal_consultant: {
    modules: ["casePortal", "bodyRemoval"],
    capabilities: ["caseInitialCreate", "caseWrite", "bodyRemovalAssign"],
  },
  funeral_planner: {
    modules: [
      "casePortal",
      "funeralPlanning",
      "specialServices",
      "warehouse",
      "quotations",
      "memorials",
      "tombstone",
      "catering",
      "ifasReports",
    ],
    capabilities: [
      "caseInitialCreate",
      "caseWrite",
      "caseConvertPolicy",
      "caseSubmit",
      "quotationWrite",
      "warehousePreparationWrite",
      "warehouseChecklistWrite",
      "warehouseReportWrite",
    ],
  },
  removal_call_centre_agent: {
    modules: ["bodyRemoval"],
    capabilities: ["bodyRemovalAssign"],
  },
  ifas_driver: {
    modules: ["bodyRemoval", "bodyDispatch", "drivers"],
    capabilities: ["bodyRemovalOperate", "bodyDispatchOperate"],
  },
  driver: {
    modules: ["bodyRemoval", "bodyDispatch", "drivers"],
    capabilities: ["bodyRemovalOperate", "bodyDispatchOperate"],
  },
  general_assistant: {
    modules: ["mortuary", "bodyDispatch"],
    capabilities: ["bodyDispatchOperate", "mortuaryAssist"],
  },
  mortuary_supervisor: {
    modules: ["casePortal", "mortuary", "bodyDispatch", "specialServices", "ifasReports"],
    capabilities: [
      "caseAssignSomeone",
      "plannerBranchManage",
      "bodyDispatchOperate",
      "bodyDispatchApprove",
      "mortuaryAssist",
      "mortuaryManage",
      "mortuaryApprove",
    ],
  },
  funeral_undertaker: {
    modules: ["casePortal", "bodyDispatch"],
    capabilities: ["bodyDispatchOperate"],
  },
  branch_manager: {
    modules: ["casePortal", "bodyDispatch", "mortuary", "revenue", "ifasReports"],
    capabilities: ["caseAssignSomeone", "plannerBranchManage", "revenueView"],
  },
  operations_manager: {
    modules: [
      "casePortal",
      "specialServices",
      "bodyRemoval",
      "mortuary",
      "warehouse",
      "bodyDispatch",
      "fleet",
      "drivers",
      "vehicles",
      "ifasReports",
    ],
    capabilities: [
      "caseConvertPolicy",
      "caseAssignSomeone",
      "plannerBranchManage",
      "operationalLocationManage",
      "bodyDispatchApprove",
      "mortuaryAssist",
      "mortuaryApprove",
      "warehousePreparationApprove",
      "warehouseChecklistApprove",
      "warehouseReportSubmit",
      "fleetManage",
    ],
  },
  funeral_supervisor: {
    modules: ["casePortal", "funeralPlanning", "ifasReports"],
    capabilities: ["caseAssignSomeone", "plannerBranchManage"],
  },
  supervisor: {
    modules: ["casePortal", "funeralPlanning"],
    capabilities: ["caseAssignSomeone", "plannerBranchManage"],
  },
  warehouse_supervisor: {
    modules: ["warehouse", "ifasReports"],
    capabilities: [
      "warehousePreparationWrite",
      "warehousePreparationApprove",
      "warehouseChecklistWrite",
      "warehouseChecklistApprove",
      "warehouseReportWrite",
      "warehouseReportSubmit",
      "inventoryManage",
    ],
  },
  security_officer: {
    modules: ["warehouse"],
    capabilities: ["warehouseChecklistApprove"],
  },
  inventory_stock_control_officer: {
    modules: ["warehouse", "ifasReports"],
    capabilities: ["warehouseReportSubmit", "inventoryManage"],
  },
  fleet_centre_coordinator: {
    modules: ["bodyDispatch", "fleet", "drivers", "vehicles", "specialServices", "ifasReports"],
    capabilities: ["bodyDispatchOperate", "fleetManage"],
  },
  finance_officer: {
    modules: ["revenue", "catalogue", "quotations", "casePortal"],
    capabilities: [
      "revenueView",
      "quotationWrite",
      "quotationApprove",
      "invoiceIssue",
      "paymentConfirm",
      "catalogueManage",
    ],
  },
};

const fallbackAliases: Record<string, string> = {
  admin: "ifas_administrator",
  ifas_admin: "ifas_administrator",
  ifaas_administrator: "ifas_administrator",
  gcoo: "ifas_gcoo",
  gceo: "ifas_gceo",
  system_administrator: "ifas_administrator",
  ifas_system_administrator: "ifas_administrator",
  administrator: "ifas_administrator",
  ifaas_driver: "ifas_driver",
  ifas_removal_consultant: "removal_consultant",
  ifas_consultant: "removal_consultant",
  ifaas_consultant: "removal_consultant",
  ifas_consulant: "removal_consultant",
  ifas_consulatnt: "removal_consultant",
  ifaas_consulant: "removal_consultant",
  ifaas_consulatnt: "removal_consultant",
  ifas_inventory_stock_control_officer: "inventory_stock_control_officer",
  ifas_warehouse_supervisor: "warehouse_supervisor",
  ifas_funeral_undertaker: "funeral_undertaker",
  ifas_mortuary_supervisor: "mortuary_supervisor",
  ifas_general_assistant: "general_assistant",
  ifas_removal_call_centre_agent: "removal_call_centre_agent",
  ifas_funeral_planner: "funeral_planner",
  ifas_fleet_centre_coordinator: "fleet_centre_coordinator",
  fleet_center_coordinator: "fleet_centre_coordinator",
  ifas_fleet_center_coordinator: "fleet_centre_coordinator",
  ifas_operations_manager: "operations_manager",
  ifas_branch_manager: "branch_manager",
  ifas_security_officer: "security_officer",
  ifas_driver: "ifas_driver",
  ifas_finance_officer: "finance_officer",
  finance_officer_ifas: "finance_officer",
};

const resolveRoleKey = (role: unknown) => {
  const normalizedRole = normalizeIfasRole(role);
  return fallbackAliases[normalizedRole] || normalizedRole;
};

export const isKnownIfasRole = (role: unknown) =>
  Boolean(ifasRoleConfig[resolveRoleKey(role)]);

export const isKnownIfasDriverRole = (role: unknown) =>
  ["ifas_driver", "driver"].includes(resolveRoleKey(role));

export const canAccessIfasModule = (role: unknown, moduleKey: IfasModuleKey) =>
  Boolean(ifasRoleConfig[resolveRoleKey(role)]?.modules.includes(moduleKey));

export const hasIfasCapability = (role: unknown, capability: IfasCapability) =>
  Boolean(
    ifasRoleConfig[resolveRoleKey(role)]?.capabilities.includes(capability),
  );

export const isRemovalCaseInitiatorRole = (role: unknown) =>
  resolveRoleKey(role) === "removal_consultant";

export const getIfasAccessProfile = (role: unknown) =>
  ifasRoleConfig[resolveRoleKey(role)] || {
    modules: [],
    capabilities: [],
  };

const reportAccessByRole: Record<string, IfasReportKey[]> = {
  ifas_administrator: ["funeral", "planner", "mortuary", "mortuaryTrends", "services", "fleet", "warehouse", "inventoryMovements", "suppliers", "customers", "feedback", "executive"],
  funeral_planner: ["funeral", "planner", "services", "feedback"],
  mortuary_supervisor: ["mortuary", "mortuaryTrends"],
  branch_manager: ["funeral", "planner", "mortuary", "mortuaryTrends", "services", "feedback"],
  operations_manager: ["funeral", "planner", "mortuary", "mortuaryTrends", "services", "fleet", "warehouse", "inventoryMovements", "suppliers", "customers", "feedback"],
  warehouse_supervisor: ["warehouse", "inventoryMovements", "suppliers"],
  inventory_stock_control_officer: ["warehouse", "inventoryMovements", "suppliers"],
  fleet_centre_coordinator: ["fleet"],
  ifas_gcoo: ["funeral", "planner", "mortuary", "mortuaryTrends", "services", "fleet", "warehouse", "inventoryMovements", "suppliers", "customers", "feedback", "executive"],
  ifas_gceo: ["funeral", "planner", "mortuary", "mortuaryTrends", "services", "fleet", "warehouse", "inventoryMovements", "suppliers", "customers", "feedback", "executive"],
  executive: ["funeral", "planner", "mortuary", "mortuaryTrends", "services", "fleet", "warehouse", "inventoryMovements", "suppliers", "customers", "feedback", "executive"],
};

export const canAccessIfasReport = (role: unknown, reportKey: IfasReportKey) =>
  Boolean(reportAccessByRole[resolveRoleKey(role)]?.includes(reportKey));

export const getIfasReportAccess = (role: unknown) =>
  reportAccessByRole[resolveRoleKey(role)] || [];

